/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getInsurancePolicies } from 'app/entities/insurance-policy/insurance-policy.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { getEntities as getHospitals } from 'app/entities/hospital/hospital.reducer';
import { getEntities as getHospitalBills } from 'app/entities/hospital-bill/hospital-bill.reducer';
import { PatientType } from 'app/shared/model/enumerations/patient-type.model';
import { InsuranceClaimStatus } from 'app/shared/model/enumerations/insurance-claim-status.model';
import { PaymentStatus } from 'app/shared/model/enumerations/payment-status.model';
import { createEntity, getEntity, reset, updateEntity } from './insurance-claim.reducer';

export const InsuranceClaimUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const insurancePolicies = useAppSelector(state => state.insurancePolicy.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const hospitals = useAppSelector(state => state.hospital.entities);
  const hospitalBills = useAppSelector(state => state.hospitalBill.entities);
  const insuranceClaimEntity = useAppSelector(state => state.insuranceClaim.entity);
  const loading = useAppSelector(state => state.insuranceClaim.loading);
  const updating = useAppSelector(state => state.insuranceClaim.updating);
  const updateSuccess = useAppSelector(state => state.insuranceClaim.updateSuccess);
  const patientTypeValues = Object.keys(PatientType);
  const insuranceClaimStatusValues = Object.keys(InsuranceClaimStatus);
  const paymentStatusValues = Object.keys(PaymentStatus);

  const handleClose = () => {
    navigate(`/insurance-claim${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getInsurancePolicies({}));
    dispatch(getGlucoUsers({}));
    dispatch(getHospitals({}));
    dispatch(getHospitalBills({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.dateFrom !== undefined && typeof values.dateFrom !== 'number') {
      values.dateFrom = Number(values.dateFrom);
    }
    if (values.dateTo !== undefined && typeof values.dateTo !== 'number') {
      values.dateTo = Number(values.dateTo);
    }
    if (values.claimedTotal !== undefined && typeof values.claimedTotal !== 'number') {
      values.claimedTotal = Number(values.claimedTotal);
    }
    if (values.approvedTotal !== undefined && typeof values.approvedTotal !== 'number') {
      values.approvedTotal = Number(values.approvedTotal);
    }
    if (values.dateProcessed !== undefined && typeof values.dateProcessed !== 'number') {
      values.dateProcessed = Number(values.dateProcessed);
    }
    values.microSec = convertDateTimeToServer(values.microSec);
    values.timestampEntry = convertDateTimeToServer(values.timestampEntry);
    values.lastUpdated = convertDateTimeToServer(values.lastUpdated);
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...insuranceClaimEntity,
      ...values,
      policy: insurancePolicies.find(it => it.id.toString() === values.policy?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
      location: hospitals.find(it => it.id.toString() === values.location?.toString()),
      bill: hospitalBills.find(it => it.id.toString() === values.bill?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          microSec: displayDefaultDateTime(),
          timestampEntry: displayDefaultDateTime(),
          lastUpdated: displayDefaultDateTime(),
        }
      : {
          type: 'OP',
          status: 'REJECTED',
          paymentStatus: 'ENTERED',
          ...insuranceClaimEntity,
          microSec: convertDateTimeFromServer(insuranceClaimEntity.microSec),
          timestampEntry: convertDateTimeFromServer(insuranceClaimEntity.timestampEntry),
          lastUpdated: convertDateTimeFromServer(insuranceClaimEntity.lastUpdated),
          policy: insuranceClaimEntity?.policy?.id,
          patient: insuranceClaimEntity?.patient?.id,
          location: insuranceClaimEntity?.location?.id,
          bill: insuranceClaimEntity?.bill?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.insuranceClaim.home.createOrEditLabel" data-cy="InsuranceClaimCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.insuranceClaim.home.createOrEditLabel">Create or edit a InsuranceClaim</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="insurance-claim-id"
                  label={translate('tinyMedicRestApp.insuranceClaim.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.claimCode')}
                id="insurance-claim-claimCode"
                name="claimCode"
                data-cy="claimCode"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.dateFrom')}
                id="insurance-claim-dateFrom"
                name="dateFrom"
                data-cy="dateFrom"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.dateTo')}
                id="insurance-claim-dateTo"
                name="dateTo"
                data-cy="dateTo"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.adjustment')}
                id="insurance-claim-adjustment"
                name="adjustment"
                data-cy="adjustment"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.claimedTotal')}
                id="insurance-claim-claimedTotal"
                name="claimedTotal"
                data-cy="claimedTotal"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.approvedTotal')}
                id="insurance-claim-approvedTotal"
                name="approvedTotal"
                data-cy="approvedTotal"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.dateProcessed')}
                id="insurance-claim-dateProcessed"
                name="dateProcessed"
                data-cy="dateProcessed"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.explanation')}
                id="insurance-claim-explanation"
                name="explanation"
                data-cy="explanation"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.rejectionReason')}
                id="insurance-claim-rejectionReason"
                name="rejectionReason"
                data-cy="rejectionReason"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.guaranteeId')}
                id="insurance-claim-guaranteeId"
                name="guaranteeId"
                data-cy="guaranteeId"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.externalId')}
                id="insurance-claim-externalId"
                name="externalId"
                data-cy="externalId"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.type')}
                id="insurance-claim-type"
                name="type"
                data-cy="type"
                type="select"
              >
                {patientTypeValues.map(patientType => (
                  <option value={patientType} key={patientType}>
                    {translate(`tinyMedicRestApp.PatientType.${patientType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.status')}
                id="insurance-claim-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {insuranceClaimStatusValues.map(insuranceClaimStatus => (
                  <option value={insuranceClaimStatus} key={insuranceClaimStatus}>
                    {translate(`tinyMedicRestApp.InsuranceClaimStatus.${insuranceClaimStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.paymentStatus')}
                id="insurance-claim-paymentStatus"
                name="paymentStatus"
                data-cy="paymentStatus"
                type="select"
              >
                {paymentStatusValues.map(paymentStatus => (
                  <option value={paymentStatus} key={paymentStatus}>
                    {translate(`tinyMedicRestApp.PaymentStatus.${paymentStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.active')}
                id="insurance-claim-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.microSec')}
                id="insurance-claim-microSec"
                name="microSec"
                data-cy="microSec"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.timestampEntry')}
                id="insurance-claim-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.lastUpdated')}
                id="insurance-claim-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.insuranceClaim.cursor')}
                id="insurance-claim-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="insurance-claim-policy"
                name="policy"
                data-cy="policy"
                label={translate('tinyMedicRestApp.insuranceClaim.policy')}
                type="select"
                required
              >
                <option value="" key="0" />
                {insurancePolicies
                  ? insurancePolicies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="insurance-claim-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.insuranceClaim.patient')}
                type="select"
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="insurance-claim-location"
                name="location"
                data-cy="location"
                label={translate('tinyMedicRestApp.insuranceClaim.location')}
                type="select"
              >
                <option value="" key="0" />
                {hospitals
                  ? hospitals.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="insurance-claim-bill"
                name="bill"
                data-cy="bill"
                label={translate('tinyMedicRestApp.insuranceClaim.bill')}
                type="select"
              >
                <option value="" key="0" />
                {hospitalBills
                  ? hospitalBills.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/insurance-claim" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InsuranceClaimUpdate;
